import React, { useEffect, useRef } from 'react';
import { ChevronDown, Briefcase } from 'lucide-react';
import schwabLogo from '@/assets/images/brokers/schwab.svg';
import tradierLogo from '@/assets/images/brokers/tradier.svg';
import tastyLogo from '@/assets/images/brokers/tastytrade.svg';

const Hero = () => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (dashboardRef.current) {
      observer.observe(dashboardRef.current);
    }
    
    return () => {
      if (dashboardRef.current) {
        observer.unobserve(dashboardRef.current);
      }
    };
  }, []);

  return (
    <section className="pt-28 pb-20 relative overflow-hidden" id="hero">
      <div className="absolute inset-0 pattern-grid -z-10"></div>
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-up247-100 rounded-full filter blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-up247-50 rounded-full filter blur-3xl opacity-30"></div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="text-center mb-8">
          <span className="inline-block py-1 px-3 bg-up247-50 text-up247-700 text-xs font-medium rounded-full mb-4 animate-fade-in">
            A unified trading experience
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight md:leading-tight text-gray-900 mb-6 animate-fade-in animate-delay-100 text-balance max-w-4xl mx-auto">
            One dashboard for all your <span className="heading-gradient">trading accounts</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto animate-fade-in animate-delay-200 text-balance">
            Connect your brokerage accounts, execute trades with one click, and make smarter decisions with AI assistance.
          </p>

        </div>
        
        <div 
          ref={dashboardRef}
          className="glass-strong rounded-2xl shadow-2xl opacity-0 translate-y-10 transition-all duration-700 max-w-6xl mx-auto overflow-hidden"
        >
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="col-span-2 bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Portfolio Performance</h3>
                  <div className="text-xs text-up247-600 bg-up247-50 px-2 py-1 rounded">Today</div>
                </div>
                <div className="h-52 flex items-center justify-center">
                  <img 
                    src="https://images.unsplash.com/photo-1498050108023-c5249f4df085" 
                    alt="Portfolio Performance" 
                    className="w-full h-full object-cover rounded-lg opacity-80"
                  />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-gray-500">Total Value</div>
                    <div className="text-lg font-semibold">$184,521</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Daily Change</div>
                    <div className="text-lg font-semibold text-green-600">+$4,321</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">% Change</div>
                    <div className="text-lg font-semibold text-green-600">+2.34%</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-sm font-medium">Open Positions</div>
                    <div className="text-lg font-semibold">26</div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm relative">
                  <h3 className="text-sm font-medium mb-3">AI Insight</h3>
                  <p className="text-xs text-gray-700">Consider rolling your AAPL calls forward to capture earnings announcement next week.</p>
                </div>
                
                <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-sm font-medium">Expiring Soon</div>
                    <div className="text-lg font-semibold text-amber-600">3</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span>AAPL $215 Call</span>
                      <span className="text-gray-500">3 days</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>SPY $500 Put</span>
                      <span className="text-gray-500">5 days</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm flex items-center justify-center">
                <img src={tradierLogo} alt="Tradier" className="h-8 opacity-80" />
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm flex items-center justify-center">
                <img src={tastyLogo} alt="TastyTrade" className="h-8 opacity-80" />
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm flex items-center justify-center">
                <img src={schwabLogo} alt="Charles Schwab" className="h-8 opacity-80" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
